import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { XMarkIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Button } from "./Button";

// ─── FormModal ──────────────────────────────────────────────────────────────
// Root wrapper: backdrop + animated panel
// Props: open, onClose, maxWidth ("sm"|"md"|"lg"|"xl"|"2xl"), children
export const FormModal = ({ open, onClose, maxWidth = "xl", children }) => {
  const widthMap = {
    sm: "max-w-sm",
    md: "max-w-md",
    lg: "max-w-lg",
    xl: "max-w-xl",
    "2xl": "max-w-2xl",
  };

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            key="fm-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
            onClick={onClose}
          />
          {/* Panel */}
          <motion.div
            key="fm-panel"
            initial={{ opacity: 0, y: -24, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.97 }}
            transition={{ duration: 0.22, ease: "easeOut" }}
            className={`relative bg-white rounded-2xl shadow-2xl w-full ${widthMap[maxWidth]} flex flex-col max-h-[92vh] overflow-hidden`}
          >
            {children}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};

// ─── FormModalHeader ─────────────────────────────────────────────────────────
// Gradient top bar with icon, title, subtitle, and close button
// Props: icon (heroicon), title, subtitle, onClose
export const FormModalHeader = ({ icon: Icon, title, subtitle, onClose }) => (
  <div className="flex items-center gap-4 px-6 py-5 bg-gradient-to-r from-primary-600 to-primary-500 rounded-t-2xl flex-shrink-0">
    {Icon && (
      <div className="flex-shrink-0 flex items-center justify-center w-10 h-10 bg-white/20 rounded-xl">
        <Icon className="w-5 h-5 text-white" />
      </div>
    )}
    <div className="flex-1 min-w-0">
      <h3 className="text-base font-semibold text-white leading-tight">{title}</h3>
      {subtitle && <p className="text-xs text-primary-100 mt-0.5">{subtitle}</p>}
    </div>
    <button
      type="button"
      onClick={onClose}
      className="flex-shrink-0 p-1.5 text-white/70 hover:text-white hover:bg-white/20 rounded-lg transition-colors"
    >
      <XMarkIcon className="w-5 h-5" />
    </button>
  </div>
);

// ─── FormModalBody ────────────────────────────────────────────────────────────
// Scrollable form body
// Props: id (form id for footer submit), onSubmit, children
export const FormModalBody = ({ id, onSubmit, children }) => (
  <form id={id} onSubmit={onSubmit} className="flex-1 overflow-y-auto">
    <div className="px-6 py-5 space-y-5">{children}</div>
  </form>
);

// ─── FormModalSection ─────────────────────────────────────────────────────────
// Labelled section divider inside the body
// Props: title, children
export const FormModalSection = ({ title, children }) => (
  <div>
    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-3">{title}</p>
    {children}
  </div>
);

// ─── FormModalField ───────────────────────────────────────────────────────────
// Label + optional leading icon wrapper for any input/select/textarea
// Props: label, required, icon (heroicon), iconTop (bool, for textarea), children
export const FormModalField = ({ label, required, icon: Icon, iconTop = false, children }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5">
      {label} {required && <span className="text-red-400">*</span>}
    </label>
    <div className="relative">
      {Icon && (
        <Icon
          className={`absolute left-3 ${
            iconTop ? "top-3" : "top-1/2 -translate-y-1/2"
          } w-4 h-4 text-gray-400 pointer-events-none z-10`}
        />
      )}
      {children}
    </div>
  </div>
);

// ─── formInputCls ─────────────────────────────────────────────────────────────
// Shared Tailwind class string for inputs / selects / textareas
// hasIcon: adds left padding for the icon; extraCls: any additional classes
export const formInputCls = (hasIcon = true, extraCls = "") =>
  `w-full ${hasIcon ? "pl-9" : "pl-3"} pr-3 py-2.5 border border-gray-200 rounded-lg text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors bg-gray-50 focus:bg-white ${extraCls}`.trim();

// ─── FormModalToggle ──────────────────────────────────────────────────────────
// Active-status toggle row (icon + label + toggle switch)
// Props: checked, onChange, label, activeText, inactiveText
export const FormModalToggle = ({
  checked,
  onChange,
  label = "Active Status",
  activeText = "Active and visible",
  inactiveText = "Disabled and hidden",
}) => (
  <div className="flex items-center justify-between px-4 py-3 bg-gray-50 rounded-xl border border-gray-200">
    <div className="flex items-center gap-3">
      <div
        className={`flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center ${
          checked ? "bg-green-100" : "bg-gray-200"
        }`}
      >
        <CheckCircleIcon className={`w-4 h-4 ${checked ? "text-green-600" : "text-gray-400"}`} />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-900">{label}</p>
        <p className="text-xs text-gray-500">{checked ? activeText : inactiveText}</p>
      </div>
    </div>
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
        checked ? "bg-primary-600" : "bg-gray-300"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-lg transform ring-0 transition duration-200 ease-in-out ${
          checked ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  </div>
);

// ─── FormModalFooter ──────────────────────────────────────────────────────────
// Sticky footer: required hint / cancel / submit
// Props: formId, onCancel, saving, isEdit, submitLabel (e.g. "Framework")
export const FormModalFooter = ({ formId, onCancel, saving, isEdit, submitLabel = "" }) => (
  <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100 bg-gray-50/80 rounded-b-2xl flex-shrink-0">
    <p className="text-xs text-gray-400">
      <span className="text-red-400">*</span> Required fields
    </p>
    <div className="flex items-center gap-3">
      <Button type="button" variant="secondary" size="sm" onClick={onCancel}>
        Cancel
      </Button>
      <Button
        type="submit"
        form={formId}
        variant="primary"
        size="sm"
        disabled={saving}
        loading={saving}
      >
        {saving
          ? "Saving…"
          : isEdit
          ? `Update${submitLabel ? " " + submitLabel : ""}`
          : `Create${submitLabel ? " " + submitLabel : ""}`}
      </Button>
    </div>
  </div>
);

export default FormModal;
